'use client'

import { useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import {
  ListIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  XIcon,
} from '@phosphor-icons/react'
import { cn } from '@/lib/tailwind'
import { SidebarNav } from './sidebar-nav'
import {
  MOBILE_DRAWER_BACKDROP_TEST_ID,
  MOBILE_DRAWER_TEST_ID,
  MOBILE_TOP_BAR_TEST_ID,
} from './test-ids'

// ---------------------------------------------------------------------------
// Page title from pathname
// ---------------------------------------------------------------------------

function usePageTitle(): string {
  const pathname = usePathname()

  if (pathname === '/app' || pathname === '/') return 'Today'
  if (pathname.startsWith('/app/upcoming')) return 'Upcoming'
  if (pathname.startsWith('/app/inbox')) return 'Inbox'
  if (pathname.startsWith('/app/projects')) return 'Projects'
  if (pathname.startsWith('/app/journal')) return 'Journal'
  if (pathname.startsWith('/app/movies')) return 'Movies'
  if (pathname.startsWith('/app/books')) return 'Books'
  if (pathname.startsWith('/app/music')) return 'Music'
  if (pathname.startsWith('/app/people')) return 'People'
  if (pathname.startsWith('/app/settings')) return 'Settings'
  return 'Konstantout'
}

// ---------------------------------------------------------------------------
// MobileTopBar
// ---------------------------------------------------------------------------

interface MobileTopBarProps {
  onMenuOpen: () => void
  onSearchOpen?: () => void
  onCaptureOpen?: () => void
}

export function MobileTopBar({
  onMenuOpen,
  onSearchOpen,
  onCaptureOpen,
}: MobileTopBarProps) {
  const title = usePageTitle()

  return (
    <header
      className={cn(
        'border-border/50 bg-background flex h-12 w-full items-center border-b px-3',
        'safe-area-inset-top sm:hidden',
      )}
      aria-label="Mobile navigation"
      data-testid={MOBILE_TOP_BAR_TEST_ID}
    >
      {/* Left: hamburger */}
      <button
        type="button"
        onClick={onMenuOpen}
        className={cn(
          'flex h-10 w-10 items-center justify-center rounded-md',
          'text-muted-foreground hover:text-foreground transition-colors',
          'focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none',
        )}
        aria-label="Open menu"
      >
        <ListIcon className="h-5 w-5" weight="regular" />
      </button>

      {/* Center: page title */}
      <span className="text-foreground flex-1 text-center text-sm font-medium">
        {title}
      </span>

      {/* Right: search + capture */}
      <div className="flex items-center gap-0.5">
        <button
          type="button"
          onClick={onSearchOpen}
          className={cn(
            'flex h-10 w-10 items-center justify-center rounded-md',
            'text-muted-foreground hover:text-foreground transition-colors',
            'focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none',
          )}
          aria-label="Search"
        >
          <MagnifyingGlassIcon className="h-5 w-5" weight="regular" />
        </button>
        <button
          type="button"
          onClick={onCaptureOpen}
          className={cn(
            'flex h-10 w-10 items-center justify-center rounded-md',
            'text-muted-foreground hover:text-foreground transition-colors',
            'focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none',
          )}
          aria-label="New capture"
        >
          <PlusIcon className="h-5 w-5" weight="bold" />
        </button>
      </div>
    </header>
  )
}

// ---------------------------------------------------------------------------
// MobileDrawer
// ---------------------------------------------------------------------------

interface MobileDrawerProps {
  open: boolean
  onClose: () => void
}

export function MobileDrawer({ open, onClose }: MobileDrawerProps) {
  const pathname = usePathname()
  const prevPathnameRef = useRef(pathname)

  // Close on route change
  useEffect(() => {
    if (prevPathnameRef.current !== pathname && open) {
      onClose()
    }
    prevPathnameRef.current = pathname
  }, [pathname, open, onClose])

  // Body scroll lock
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [open])

  // Auto-close on resize past 640px
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 640px)')
    function handleChange(e: MediaQueryListEvent) {
      if (e.matches && open) {
        onClose()
      }
    }
    mq.addEventListener('change', handleChange)
    return () => mq.removeEventListener('change', handleChange)
  }, [open, onClose])

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 z-40 bg-black/50 transition-opacity duration-250 ease-out sm:hidden',
          open ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
        onClick={onClose}
        aria-hidden="true"
        data-testid={MOBILE_DRAWER_BACKDROP_TEST_ID}
      />

      {/* Drawer panel */}
      <aside
        className={cn(
          'bg-background fixed inset-y-0 left-0 z-50 flex w-72 flex-col shadow-xl',
          'transition-transform duration-250 ease-out sm:hidden',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
        aria-label="Mobile sidebar"
        data-testid={MOBILE_DRAWER_TEST_ID}
        role="dialog"
        aria-modal={open}
      >
        {/* Drawer header */}
        <div className="flex items-center justify-between px-3 pt-4 pb-2">
          <span className="font-logo text-xl tracking-tight">
            konstant<span className="text-brand-accent">out</span>
          </span>
          <button
            type="button"
            onClick={onClose}
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-md',
              'text-muted-foreground hover:text-foreground transition-colors',
              'focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none',
            )}
            aria-label="Close menu"
          >
            <XIcon className="h-4 w-4" weight="bold" />
          </button>
        </div>

        {/* Nav content */}
        <div className="flex-1 overflow-y-auto px-3 py-2">
          <SidebarNav collapsed={false} />
        </div>
      </aside>
    </>
  )
}
